import React from 'react';
import { Store } from 'redux';
import {
  AppContent,
  ErrorBoundary,
  LinkWithButtonStyle,
  MEDIA_BREAKPOINTS,
  MEDIUM_SCREEN_SIZE,
  SMALL_SCREEN_SIZE,
  SecondarySectionTitle,
  SectionHeader,
  SectionWithTitle,
  OneVCNineHLC,
  ScrollableLinkImages,
  ScrollableArticleList,
  Banner,
  baseUnit,
  WideScrollableArticleList,
  Carousel,
  SwimsuitPlayer,
} from 'mm-ui-components';
import { useSelector } from 'react-redux';
import { StyleSheet, css } from 'aphrodite/no-important';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import { Theme } from '../../components/connectedComponents/Theme';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import {
  getSectionArticlesByKey,
  getBannerImage,
  getMoreButtonTextByKey,
  getMetadataDescription,
  getMetadataImage,
  getMetadataTitle,
  getHreflangData,
  getSectionTitleByKey,
  getMoreButtonURLByKey,
  getTopSectionTitle,
  getSectionImageLinks,
  getBannerLink,
  getFeaturedArticle,
} from '../../store/template/homePage/homePageMiami/homePageMiami.selectors';
import { SECTION_NAMES } from '../templates.const';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { Page } from '../Page';
import { getUseFormatMinutesHoursAgo } from '../../store/config/config.selectors';
import { AdditionalStyleSheet } from '../templates.utils';

interface HomePageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const getArticlesInTemplate = (state: AppState) => {
  const topSectionArticles = getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION)(state) || [];
  const contentSection1Articles = getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_1)(state) || [];
  const contentSection2Articles = getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_2)(state) || [];

  return [
    ...topSectionArticles,
    ...contentSection1Articles,
    ...contentSection2Articles,
  ];
};

export const HomePageMiamiComponent: React.FunctionComponent<HomePageProps> = ({
  store,
  errorProvider,
}) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <HomePageMiamiContent />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  sectionsWrapper: {
    [MEDIA_BREAKPOINTS.large]: {
      gridRowGap: `${baseUnit * 3}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridRowGap: `${baseUnit * 2}px`,
      maxWidth: `${MEDIUM_SCREEN_SIZE}px`,
      margin: '0 auto',
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridRowGap: `${baseUnit * 2}px`,
      maxWidth: `${SMALL_SCREEN_SIZE}px`,
      margin: '0 auto',
      paddingBottom: `${baseUnit}px`,
    },
    display: 'grid',
    gridTemplateColumns: '1fr',
    marginBottom: `${baseUnit * 0.5}px`,
    justifyItems: 'center',
  },
  topSection: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    alignItems: 'center',
    marginTop: '40px',
    [MEDIA_BREAKPOINTS.medium]: {
      padding: `0 ${baseUnit}px`,
    },
  },
});

const sectionsStyle = StyleSheet.create({
  style: {
    [MEDIA_BREAKPOINTS.medium]: {
      padding: `0 ${baseUnit}px`,
    },
  },
}) as AdditionalStyleSheet;

export const HomePageMiamiContent = () => {
  const topSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION)) || [];
  const topSectionMoreButtonURL = useSelector(
    getMoreButtonURLByKey(SECTION_NAMES.TOP_SECTION),
  );
  const topSectionMoreButtonText = useSelector(
    getMoreButtonTextByKey(SECTION_NAMES.TOP_SECTION),
  );
  const topSectionImage = useSelector(
    getBannerImage(SECTION_NAMES.TOP_SECTION),
  );
  const topSectionImageLink = useSelector(
    getBannerLink(SECTION_NAMES.TOP_SECTION),
  );
  const topSectionTitle = useSelector(
    getTopSectionTitle(SECTION_NAMES.TOP_SECTION),
  );
  const featuredArticle = useSelector(getFeaturedArticle(SECTION_NAMES.TOP_SECTION)) || [];
  const contentSection1Title = useSelector(
    getSectionTitleByKey(SECTION_NAMES.CONTENT_SECTION_1),
  );
  const contentSection1Articles = useSelector(
    getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_1),
  );
  const contentSection2Title = useSelector(
    getSectionTitleByKey(SECTION_NAMES.CONTENT_SECTION_2),
  );
  const contentSection2Articles = useSelector(
    getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_2),
  );
  const contentSection3ImageLinks = useSelector(getSectionImageLinks(SECTION_NAMES.CONTENT_SECTION_3));
  const contentSection4Title = useSelector(
    getSectionTitleByKey(SECTION_NAMES.CONTENT_SECTION_4),
  );
  const contentSection4ImageLinks = useSelector(
    getSectionImageLinks(SECTION_NAMES.CONTENT_SECTION_4),
  );
  const contentSection5Title = useSelector(
    getSectionTitleByKey(SECTION_NAMES.CONTENT_SECTION_5),
  );
  const contentSection5ImageLinks = useSelector(getSectionImageLinks(SECTION_NAMES.CONTENT_SECTION_5));

  const metaTitle = useSelector(getMetadataTitle);
  const metaDescription = useSelector(getMetadataDescription);
  const pageImage = useSelector(getMetadataImage);
  const articles = useSelector(getArticlesInTemplate);
  const hreflangData = useSelector(getHreflangData) || [];
  const style = createStyle();
  const useFormatMinutesHoursAgo = useSelector(getUseFormatMinutesHoursAgo);
  const customRatios = { small: { x: 1, y: 1 }, medium: { x: 1, y: 1 }, large: { x: 1, y: 1 } };

  const buttonStyle = () => StyleSheet.create({
    style: {
      marginTop: '25px',
    },
  }) as AdditionalStyleSheet;

  return (
    <>
      <MetaTags
        articles={articles}
        title={metaTitle}
        description={metaDescription}
        pageImage={pageImage}
        alternateUrls={hreflangData}
      />
      <HeadScripts />
      <Theme>
        <AppContent>
          <NavigationLayout mainWrapperConfigThemeKey="mainContent" noMarginTop>
            <div className={css(style.sectionsWrapper)}>
              <Banner
                image={{
                ...topSectionImage,
                imageWidths: {
                  large: 1080 as const,
                  medium: 720 as const,
                  small: 540 as const,
                },
                }}
                linkPath={topSectionImageLink}
              />
              <div className={css(style.topSection)}>
                <OneVCNineHLC cards={[...featuredArticle, ...topSectionArticles]} displayName={topSectionTitle || 'Latest News'} useFormatMinutesHoursAgo={useFormatMinutesHoursAgo} />
                <LinkWithButtonStyle
                  href={topSectionMoreButtonURL || undefined}
                  additionalStyle={buttonStyle()}
                >
                  {topSectionMoreButtonText}
                </LinkWithButtonStyle>
              </div>
              <WideScrollableArticleList
                cardsProps={contentSection1Articles}
                cardsSectionDisplayName={contentSection1Title}
                useFormatMinutesHoursAgo={useFormatMinutesHoursAgo}
              />
              <SectionWithTitle additionalStyle={sectionsStyle}>
                <SectionHeader moreButtonText={null} moreButtonSvg={null}>
                  <SecondarySectionTitle>
                    {contentSection2Title}
                  </SecondarySectionTitle>
                </SectionHeader>
                <ScrollableArticleList
                  cardsProps={contentSection2Articles}
                  cardTheme={{
                    showAuthorInLargeScreen: false,
                    showAuthorInMediumScreen: false,
                    showAuthorInSmallScreen: false,
                    showDescriptionInLargeScreen: true,
                    showDescriptionInMediumScreen: true,
                    showDescriptionInSmallScreen: true,
                    showMainCategoryInLargeScreen: true,
                    numberOfLinesForDescription: 10,
                    numberOfLinesForTitle: 10,
                    showMainCategoryAboveText: true,
                  }}
                />
              </SectionWithTitle>
              <SectionWithTitle additionalStyle={sectionsStyle}>
                <ScrollableLinkImages linkImages={contentSection3ImageLinks} customRatios={{ large: { x: 16, y: 9 }, medium: { x: 16, y: 9 }, small: { x: 16, y: 9 } }} />
              </SectionWithTitle>
              <SectionWithTitle additionalStyle={sectionsStyle}>
                <SectionHeader moreButtonText={null} moreButtonSvg={null}>
                  <SecondarySectionTitle>
                    {contentSection4Title}
                  </SecondarySectionTitle>
                </SectionHeader>
                <Carousel itemType="LinkImage" itemsProps={contentSection4ImageLinks} />
              </SectionWithTitle>
              <SectionWithTitle additionalStyle={sectionsStyle}>
                <SwimsuitPlayer />
              </SectionWithTitle>
              <SectionWithTitle additionalStyle={sectionsStyle}>
                <SectionHeader moreButtonText={null} moreButtonSvg={null}>
                  <SecondarySectionTitle>
                    {contentSection5Title}
                  </SecondarySectionTitle>
                </SectionHeader>
                <ScrollableLinkImages linkImages={contentSection5ImageLinks} customRatios={customRatios} />
              </SectionWithTitle>
            </div>
          </NavigationLayout>
        </AppContent>
      </Theme>
    </>
  );
};
